// frontend/src/components/auth/SubscriptionSelection.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Check,
  CreditCard,
  Lock,
  AlertCircle,
  Tag,
  Clock,
  Loader,
  Star,
  TrendingUp,
  Shield,
  Zap,
  Truck,       // ADD: Fleet icon
  Building2,   // ADD: For enterprise
  Users        // ADD: For growth
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { paymentAPI, promoCodeAPI, authAPI } from '../../services/api';
import { useAuth } from './AuthProvider';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

// Subscription Plans Component - UPDATED with Fleet Manager plans
const SubscriptionPlans = ({ userType, selectedPlan, onPlanSelect }) => {
  // Mechanic plans
  const mechanicPlans = [
    {
      id: 'basic',
      name: 'Mechanic Pro',
      price: 29.99,
      yearlyPrice: 249.00,
      features: [
        'Job Management Tools',
        'Basic vehicle profiles',
        'Price comparison',
        'Mobile app access',
        'Email support',
        'Customer database management'
      ],
      recommended: true
    },
    {
      id: 'professional',
      name: 'Mechanic Premium',
      price: 49.99,
      yearlyPrice: 399.90,
      features: [
        'Advanced diagnostics',
        'AI-powered recommendations',
        'Full job management tools',
        'Quote generation',
        'Priority support',
        'API access',
        'Advanced reporting',
        'Invoicing and billing'
      ],
      recommended: false
    }
  ];

  // Consumer plans
  const consumerPlans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      yearlyPrice: 0,
      features: [
        '1 vehicle per account',
        'Basic vehicle information',
        '1 market value lookup per month',
        'Community support'
      ],
      recommended: false,
      isFree: true
    },
    {
      id: 'professional',
      name: 'Pro Membership',
      price: 1.99,
      yearlyPrice: 9.99,
      lifetimePrice: 49.99,
      features: [
        'Unlimited vehicles',
        'Advanced maintenance scheduling',
        'AI-powered recommendations',
        'Vehicle history reports',
        'Document Upload',
        'Unlimited market values',
        'Priority support',
        'DIY Section'
      ],
      recommended: true
    }
  ];

  // NEW: Fleet Manager plans
  const fleetManagerPlans = [
    {
      id: 'fleet_starter',
      name: 'Starter',
      price: 59.99,
      yearlyPrice: 599.90,
      icon: Truck,
      vehicleLimit: 25,
      features: [
        'Up to 25 vehicles',
        'Fleet dashboard & health score',
        'Maintenance tracking',
        'Service cost reporting',
        'Driver management',
        'Department assignment',
        'Email support',
        '14-day free trial'
      ],
      recommended: false
    },
    {
      id: 'fleet_growth',
      name: 'Growth',
      price: 99.99,
      yearlyPrice: 999.90,
      icon: Users,
      vehicleLimit: 50,
      features: [
        'Up to 50 vehicles',
        'Everything in Starter, plus:',
        'Advanced cost analytics',
        'AI maintenance predictions',
        'Bulk vehicle import',
        'Custom reporting',
        'Priority support',
        'API access'
      ],
      recommended: true
    },
    {
      id: 'fleet_professional',
      name: 'Professional',
      price: 149.99,
      yearlyPrice: 1499.90,
      icon: Building2,
      vehicleLimit: 100,
      features: [
        'Up to 100 vehicles',
        'Everything in Growth, plus:',
        'Multi-location support',
        'Advanced integrations',
        'Dedicated account manager',
        'Custom onboarding',
        'SLA guarantees',
        'Phone support'
      ],
      recommended: false
    }
  ];

  // Select plans based on user type
  const plans = userType === 'mechanic' 
    ? mechanicPlans 
    : userType === 'fleetmanager' 
      ? fleetManagerPlans 
      : consumerPlans;

  // Determine grid columns based on number of plans
  const gridCols = plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

  return (
    <div className={`grid ${gridCols} gap-6 mb-8`}>
      {plans.map((plan) => (
        <div
          key={plan.id}
          className={`relative rounded-card border-2 p-6 transition-all ${
            plan.disabled 
              ? 'opacity-50 cursor-not-allowed border-border-default bg-surface-page'
              : selectedPlan === plan.id
                ? 'border-accent bg-accent-tint cursor-pointer'
                : 'border-border-default hover:border-border-default cursor-pointer'
          } ${plan.onSale ? 'ring-2 ring-accent-border ring-offset-2' : ''}`}
          onClick={() => !plan.disabled && onPlanSelect(plan.id)}
        >
          {/* Sale Ribbon */}
          {plan.onSale && (
            <div className="absolute -top-3 -right-3 z-10">
              {/* Was an amber-to-orange gradient pill that pulsed forever. */}
              <div className="bg-accent text-white px-3 py-1 rounded-pill text-label uppercase flex items-center">
                <Zap className="w-3 h-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
                {plan.saleLabel}
              </div>
            </div>
          )}

          {/* Disabled Ribbon */}
          {plan.disabled && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
              <span className="bg-ink-faint text-white px-3 py-1 rounded-full text-xs font-semibold">
                {plan.disabledReason}
              </span>
            </div>
          )}

          {/* Recommended Badge */}
          {plan.recommended && !plan.onSale && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-accent text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                <Star className="w-3 h-3 mr-1" />
                RECOMMENDED
              </span>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {/* Fleet plans have icons */}
              {plan.icon && <plan.icon className="w-6 h-6 text-accent mr-2" />}
              <h3 className={`text-xl font-bold ${plan.disabled ? 'text-ink-faint' : 'text-ink'}`}>
                {plan.name}
              </h3>
            </div>
            {selectedPlan === plan.id && !plan.disabled && (
              <div className="bg-accent text-white rounded-full p-1">
                <Check className="w-4 h-4" />
              </div>
            )}
          </div>

          {/* Vehicle Limit Badge for Fleet Plans */}
          {plan.vehicleLimit && (
            <div className="mb-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-tint text-info">
                <Truck className="w-3 h-3 mr-1" />
                Up to {plan.vehicleLimit} vehicles
              </span>
            </div>
          )}

          <div className="mb-6">
            {/* Free Tier */}
            {plan.isFree ? (
              // The plan is already titled "Free"; repeating it as the price
              // read as a typo. State the price like every other card.
              <div className="flex items-baseline gap-2">
                <span className="text-page-title font-mono tabular-nums text-ink">$0</span>
                <span className="text-ink-muted">/month</span>
              </div>
            ) : (
              <>
                {/* Monthly Price */}
                <div className="flex items-baseline gap-2">
                  <span className={`text-page-title font-mono tabular-nums ${plan.disabled ? 'text-ink-faint' : 'text-ink'}`}>${plan.price}</span>
                  <span className="text-ink-muted">/month</span>
                </div>

                {/* Yearly Price */}
                {plan.yearlyPrice > 0 && (
                  <div className={`text-small mt-1 ${plan.disabled ? 'text-ink-faint' : 'text-ink-muted'}`}>
                    or <span className="font-mono tabular-nums">${plan.yearlyPrice}</span>/year
                    {plan.price > 0 && (
                      <span className="text-success text-small ml-1">
                        (save <span className="font-mono tabular-nums">{Math.round((1 - (plan.yearlyPrice / (plan.price * 12))) * 100)}%</span>)
                      </span>
                    )}
                  </div>
                )}

                {/* Lifetime Price */}
                {plan.lifetimePrice && (
                  <div className="mt-3 p-3 bg-surface-sunken rounded-control border border-border-default">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <TrendingUp className="w-4 h-4 text-ink-soft mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                        <span className="text-body-strong text-ink">Lifetime Access</span>
                      </div>
                      <span className="text-body-strong font-mono tabular-nums text-ink">${plan.lifetimePrice}</span>
                    </div>
                    <p className="text-small text-ink-muted mt-1">One-time payment • Limited availability</p>
                  </div>
                )}
              </>
            )}
          </div>

          <ul className="space-y-3">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className={`w-5 h-5 ${plan.disabled ? 'text-ink-faint' : 'text-success'} mr-2 flex-shrink-0 mt-0.5`} />
                <span className={`text-body ${plan.disabled ? 'text-ink-faint' : 'text-ink-muted'}`}>{feature}</span>
              </li>
            ))}
          </ul>

          {/* Disabled overlay message */}
          {plan.disabled && (
            <div className="mt-4 text-center text-small text-ink-muted italic">
              Temporarily unavailable
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Payment Form Component
const PaymentForm = ({ selectedPlan, promoCode, onSubmit, userType }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Payment system not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      await onSubmit({
        paymentMethodId: paymentMethod.id,
        billingInterval,
        promoCode
      });
    } catch (err) {
      setError(err.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Billing Interval Selection */}
      <div>
        <label className="block text-body font-medium text-ink-muted mb-2">
          Billing Frequency
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setBillingInterval('month')}
            className={`p-3 border rounded-lg text-center transition-all ${
              billingInterval === 'month'
                ? 'border-accent bg-accent-tint text-accent'
                : 'border-border-default hover:border-border-default'
            }`}
          >
            <div className="font-medium">Monthly</div>
            <div className="text-small text-ink-muted">Billed monthly</div>
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('year')}
            className={`p-3 border rounded-lg text-center transition-all ${
              billingInterval === 'year'
                ? 'border-accent bg-accent-tint text-accent'
                : 'border-border-default hover:border-border-default'
            }`}
          >
            <div className="font-medium">Yearly</div>
            <div className="text-small text-success">Save ~17%</div>
          </button>
        </div>
      </div>

      {/* Card Element */}
      <div>
        <label className="block text-body font-medium text-ink-muted mb-2">
          Card Information
        </label>
        <div className="p-4 border border-border-default rounded-lg">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-danger-tint border border-danger-border rounded-lg flex items-center text-danger">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full min-h-touch bg-accent text-white py-3 px-4 rounded-lg font-semibold hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            Start Free Trial
          </>
        )}
      </button>

      <p className="text-center text-xs text-ink-muted">
        Your card will be charged after the 30-day trial period ends.
        Cancel anytime before then and you won't be charged.
      </p>
    </form>
  );
};

// Main Subscription Selection Component
export const SubscriptionSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, clearNewRegistrationFlag } = useAuth();
  
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  
  // Get user type from auth context or localStorage
  const userType = user?.user_type || localStorage.getItem('userType') || 'consumer';

  // Set default plan based on user type
  useEffect(() => {
    if (userType === 'fleetmanager') {
      setSelectedPlan('fleet_growth'); // Default to Growth for fleet managers
    } else if (userType === 'mechanic') {
      setSelectedPlan('basic'); // Default to Mechanic Pro ($29.99) — matches the price-led acquisition ads
    } else {
      setSelectedPlan('free'); // Default to free for consumers
    }
  }, [userType]);

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const response = await promoCodeAPI.validate(promoCode, selectedPlan);
      setPromoValidation({
        valid: true,
        message: response.message || `Code applied! ${response.discount_percent}% off`,
        discount: response.discount_percent
      });
    } catch (error) {
      setPromoValidation({
        valid: false,
        message: error.response?.data?.detail || 'Invalid promo code'
      });
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePaymentSubmit = async (paymentData) => {
    try {
      // Handle free tier - no payment needed
      if (selectedPlan === 'free') {
        const response = await paymentAPI.createSubscription({
          tier: 'free',
          billing_interval: 'month',
          payment_method_id: null,
          promo_code: null
        });
        
        toast.success('Free account activated!');
        if (clearNewRegistrationFlag) {
          clearNewRegistrationFlag();
        }
        if (refreshUser) {
          try {
            await refreshUser();
          } catch (error) {
            console.warn('Failed to refresh user profile after free activation:', error);
          }
        }
        navigate('/');
        return;
      }
      
      // Create subscription with payment method
      const response = await paymentAPI.createSubscription({
        tier: selectedPlan,
        billing_interval: paymentData.billingInterval,
        payment_method_id: paymentData.paymentMethodId,
        promo_code: promoValidation?.valid ? promoCode : null
      });

      // Handle 3D Secure if required
      if (response.requires_action) {
        const stripe = await stripePromise;
        const { error } = await stripe.confirmCardPayment(response.client_secret);
        
        if (error) {
          throw new Error(error.message);
        }
      }

      // Complete registration
      if (clearNewRegistrationFlag) {
        clearNewRegistrationFlag();
      }

      // Refresh user data
      try {
        await refreshUser();
      } catch (error) {
        console.warn('Failed to refresh user profile:', error);
      }

      // Show success message
      if (response.is_trial && response.trial_end_date) {
        const trialEndDate = new Date(response.trial_end_date);
        const formattedDate = trialEndDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        toast.success(
          `Subscription started! You're on a 14-day free trial. Trial ends on ${formattedDate}.`,
          { duration: 8000 }
        );
      } else {
        toast.success('Subscription created successfully!');
      }
      
      // Redirect based on user type
      if (userType === 'fleetmanager') {
        window.location.href = '/fleet'; // Redirect to fleet dashboard
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create subscription');
      throw error;
    }
  };

  // Get header text based on user type
  const getHeaderText = () => {
    if (userType === 'fleetmanager') {
      return {
        title: 'Choose Your Fleet Plan',
        subtitle: 'Start with a 14-day free trial • No credit card charged until trial ends'
      };
    }
    return {
      title: 'Choose Your Plan',
      subtitle: 'Start with Free or choose Pro with 30-day free trial • Cancel anytime'
    };
  };

  // Check if user arrived here due to an actually-expired trial (time-based).
  // New mechanics get a no-card trial with trial_ends_at set; the banner should
  // only appear once that date has passed. Legacy free mechanics with no trial
  // stamp keep the old behavior so they still see the prompt.
  const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const isTrialExpired = userType === 'mechanic' && (
    (trialEnd && trialEnd < new Date()) ||
    (user?.subscription_tier === 'free' && !trialEnd)
  );

  const headerText = getHeaderText();

  return (
    <div className="min-h-screen bg-surface-page flex flex-col items-center py-8 px-4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Trial Expired Banner */}
        {isTrialExpired && !location.state?.newRegistration && (
          <div className="mb-6 bg-danger-tint border border-danger-border rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-danger font-semibold">Your free trial has ended</h3>
              <p className="text-danger text-body mt-1">
                To continue using The CROW and all its features, please choose a plan below and enter your payment information.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-page-title text-ink mb-4">
            {headerText.title}
          </h1>
          <div className="bg-success-tint border border-success-border rounded-lg p-4 inline-flex items-center">
            <Clock className="w-5 h-5 text-success mr-2" />
            <span className="text-success">
              {headerText.subtitle}
            </span>
          </div>
        </div>

        {/* Plan Selection */}
        <SubscriptionPlans
          userType={userType}
          selectedPlan={selectedPlan}
          onPlanSelect={setSelectedPlan}
        />

        {/* Payment Section */}
        <div className="bg-surface-card rounded-card border border-border-default shadow-elevation-1 p-6">
          <h2 className="text-section-title font-display text-ink mb-6">
            {selectedPlan === 'free' ? 'Confirm your plan' : 'Payment Information'}
          </h2>

          {/* Promo Code Section */}
          <div className="mb-6">
            {!showPromoInput ? (
              <button
                onClick={() => setShowPromoInput(true)}
                className="text-accent hover:text-accent-hover text-small font-medium flex items-center min-h-touch"
              >
                <Tag className="w-4 h-4 mr-1" />
                Add promo code
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    aria-label="Promo code"
                    className="flex-1 min-h-touch px-3 rounded-control border border-border-strong bg-surface-card text-body placeholder:text-ink-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15"
                  />
                  <button
                    onClick={validatePromoCode}
                    disabled={validatingPromo || !promoCode.trim()}
                    className="px-4 py-2 min-h-touch bg-surface-sunken text-ink-muted rounded-lg hover:bg-surface-sunken/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {validatingPromo ? 'Validating...' : 'Apply'}
                  </button>
                </div>
                {promoValidation && (
                  <div className={`text-small ${promoValidation.valid ? 'text-success' : 'text-danger'}`}>
                    {promoValidation.message}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stripe Payment Form - Only show for paid plans */}
          {selectedPlan !== 'free' && (
            <Elements stripe={stripePromise}>
              <PaymentForm
                selectedPlan={selectedPlan}
                promoCode={promoValidation?.valid ? promoCode : null}
                onSubmit={handlePaymentSubmit}
                userType={userType}
              />
            </Elements>
          )}
          
          {/* Free Tier Activation */}
          {selectedPlan === 'free' && (
            <div className="space-y-4">
              {/* The Free card above already lists these features; they were
                  repeated here in green. */}
              <button
                onClick={async () => {
                  try {
                    await handlePaymentSubmit({
                      paymentMethodId: null,
                      billingInterval: 'month',
                      promoCode: null
                    });
                  } catch (error) {
                    toast.error(error.message || 'Failed to activate free account');
                  }
                }}
                // The one primary action on this screen, so the accent: a solid
                // green fill broke "semantic colours are badges only".
                className="w-full min-h-touch bg-accent text-white py-3 px-4 rounded-control font-display font-semibold hover:bg-accent-hover transition-colors duration-state flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                <Check className="w-5 h-5 mr-2" strokeWidth={1.5} aria-hidden="true" />
                Activate Free Account
              </button>
            </div>
          )}

          {/* Trust Badges */}
          <div className="mt-6 pt-6 border-t border-border-default">
            <div className="flex items-center justify-center space-x-6 text-small text-ink-muted">
              <div className="flex items-center">
                <Shield className="w-4 h-4 mr-1" />
                Secure payment
              </div>
              <div className="flex items-center">
                <Lock className="w-4 h-4 mr-1" />
                SSL encrypted
              </div>
              <div className="flex items-center">
                <CreditCard className="w-4 h-4 mr-1" />
                PCI compliant
              </div>
            </div>
          </div>
        </div>

        {/* Terms */}
        <p className="text-center text-xs text-ink-muted mt-6">
          By subscribing, you agree to our{' '}
          <a href="/terms" className="text-accent hover:underline">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" className="text-accent hover:underline">Privacy Policy</a>.
          {userType === 'fleetmanager' 
            ? ' Your subscription will automatically renew after the 14-day trial unless cancelled.'
            : ' Your subscription will automatically renew after the trial period unless cancelled.'
          }
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSelection;
